import { User } from '.';
import { usaStatesList } from 'data/location';
import { action, computed, observable, reaction } from 'mobx';
import { app } from './../App';

// Interfaces
import {
  IState,
  IStateList,
  IStateArea,
  IStateAreaEditable,
  StateModalStatus,
  AreasFilterType,
  AssignUserStatus,
  AssignUserType,
  IAssignUser,
  IRepArea,
  IRepAssignedAreas,
  IRepAssignedAreaFromApi,
  ISelectedRep,
  IRepAssignedArea,
  IStateAreaUser,
} from 'common/interfaces/state';

// Store
export class State {
  constructor(private user: User) {}

  // Check if modal should show up
  @observable public modalStatus: StateModalStatus = StateModalStatus.Closed;

  // Fetch states when modal status changes
  private reactToModalStatusChange = reaction(
    () => this.modalStatus,
    (status: StateModalStatus) => {
      if (status === StateModalStatus.OpenOnRepAreasList) {
        this.fetchRepAreasList();
      } else if (
        status === StateModalStatus.OpenOnSpecificRepArea &&
        this.selectedRep !== null
      ) {
        this.fetchRepAssignedAreas(this.selectedRep);
      } else if (status === StateModalStatus.OpenOnStatesList) {
        this.fetchStatesList();
      } else if (
        status === StateModalStatus.OpenOnSpecificArea &&
        this.selectedState !== null
      ) {
        this.fetchStateAreasList();
      }
    }
  );

  // Reps areas
  @observable public repsAreas: IRepArea[] = [];
  @observable public repsSearch: string = '';
  @observable public selectedRep: ISelectedRep | null = null;
  @observable public repAssignedAreasDetails: IRepAssignedAreas[] = [];

  @computed public get repAssignedAreasDetailsSlpCodes(): number[] {
    return this.repAssignedAreasDetails.map((area) => area.slpcode);
  }

  @computed
  public get selectedRepAssignedAreasDetails(): IRepAssignedAreas | null {
    if (!this.selectedRep) {
      return null;
    }

    const index = this.repAssignedAreasDetailsSlpCodes.indexOf(
      this.selectedRep.slpcode
    );
    if (index === -1) {
      return null;
    }

    return this.repAssignedAreasDetails[index];
  }

  // Selected state in modal
  @observable public selectedState: {
    id: string;
    name: string;
  } | null = null;
  @observable public selectedStateIsLoading: boolean = false;

  // Search strings
  @observable public statesListSearch: string = '';
  @observable public stateAreasSearch: AreasFilterType = AreasFilterType.All;

  // All USA states
  @observable public usaStates: IState[] = Object.keys(usaStatesList).map(
    (state, i) => {
      return {
        id: state,
        name: usaStatesList[state],
        leadsCount: null,
      };
    }
  );

  // States search results
  @computed public get statesSearchedResults(): IState[] {
    const searchQuery: string = this.statesListSearch.toLowerCase().trim();

    if (searchQuery.length > 0) {
      return this.usaStates.filter((state, i) => {
        const stateId = state.id ? state.id.toLowerCase() : '';
        const stateName = state.name ? state.name.toLowerCase() : '';
        return (
          stateId.indexOf(searchQuery) !== -1 ||
          stateName.indexOf(searchQuery) !== -1
        );
      });
    } else {
      return this.usaStates;
    }
  }

  // All areas of selected state
  @observable public selectedStateAreas: IStateAreaEditable[] = [];

  // Areas of selected state filter results
  @computed public get areasSearchedResults(): IStateAreaEditable[] {
    return this.stateAreasSearch === AreasFilterType.All
      ? this.selectedStateAreas
      : this.selectedStateAreas.filter((area, i) => {
          const isAssigned: boolean =
            typeof area.assignedTo !== 'undefined' &&
            area.assignedTo &&
            area.assignedTo.length > 0;
          return this.stateAreasSearch === AreasFilterType.OnlyAssigned
            ? isAssigned
            : this.stateAreasSearch === AreasFilterType.OnlyUnassigned
            ? !isAssigned
            : false;
        });
  }

  // Areas of selected state that have been modified
  @computed public get checkedAreasSearchedResults(): IStateAreaEditable[] {
    return this.areasSearchedResults.filter((area, i) => area.checked);
  }

  @computed public get checkedAreasSearchedResultsLength(): number {
    return this.checkedAreasSearchedResults.length;
  }

  // Check if any of selected areas has sales rep assigned
  @computed public get checkedAreasSearchedResultsHasAnySalesReps(): boolean {
    return this.areasSearchedResults.some((area) => {
      return area.assignedTo && area.assignedTo.length > 0;
    });
  }

  // Status of assigning user to checked areas
  @observable public assignUser: IAssignUser = {
    isBeingAssigned: true,
    status: AssignUserStatus.None,
  };

  // Set user to area
  @action public assignUserToCheckedAreas = async (
    userId: number,
    slpCode: number,
    userName: string,
    isPrimary: boolean
  ): Promise<any> => {
    try {
      this.assignUser.status = AssignUserStatus.Saving;
      this.assignUser.isBeingAssigned = true;

      const assignLeadToRep = await app.service('provider-assign-to-rep');
      const assignLeadToRepData = {
        slpcode: slpCode,
        isPrimary,
        state: this.selectedState.id,
        areasCodes: this.checkedAreasSearchedResults.map(
          (area) => area.areaCode
        ),
      };

      const assignment = await assignLeadToRep.create(assignLeadToRepData);

      this.checkedAreasSearchedResults.map((area, i) => {
        // Filter out any reps for selected role
        area.assignedTo = area.assignedTo.filter((rep) =>
          isPrimary ? rep.primary === 0 : rep.primary === 1
        );

        if (slpCode > 0) {
          area.assignedTo.push({
            userId,
            slpcode: slpCode,
            name: userName,
            primary: isPrimary ? 1 : 0,
          });
        }
      });

      this.assignUser.status = AssignUserStatus.Success;

      setTimeout(() => {
        if (this.assignUser.status === AssignUserStatus.Success) {
          this.assignUser.status = AssignUserStatus.None;
        }
      }, 2048);

      return true;
    } catch (err) {
      this.assignUser.status = AssignUserStatus.Error;

      console.error(err);
      throw err;
    }
  };

  // Remove user from area
  @action public unassignUserFromCheckedAreas = async (
    userType: AssignUserType
  ): Promise<any> => {
    try {
      this.assignUser.status = AssignUserStatus.Saving;
      this.assignUser.isBeingAssigned = false;

      const assignLeadToRep = await app.service('provider-assign-to-rep');

      const response = await assignLeadToRep.create({
        slpcode: null,
        type: userType,
        state: this.selectedState.id,
        areaCode: this.checkedAreasSearchedResults.map((area) => area.areaCode),
      });

      this.checkedAreasSearchedResults.map((area, i) => {
        area.assignedTo = area.assignedTo.filter((rep) =>
          userType === 'primary' ? rep.primary === 0 : rep.primary === 1
        );
      });

      this.toggleAllAreaCheckboxes(false);

      this.assignUser[userType].status = AssignUserStatus.Success;

      setTimeout(() => {
        if (this.assignUser[userType].status === AssignUserStatus.Success) {
          this.assignUser[userType].status = AssignUserStatus.None;
        }
      }, 2048);

      return true;
    } catch (err) {
      this.assignUser[userType].status = AssignUserStatus.Error;

      console.error(err);
      return err;
    }
  };

  // Check or uncheck or areas in the state
  @action public toggleAllAreaCheckboxes = (check: boolean) => {
    this.areasSearchedResults.map((area, i) => {
      area.checked = check;
    });
  };

  // Check or uncheck single area
  @action public toggleAreaCheckbox = (areaCode: string, check?: boolean) => {
    let areaIndex = -1;
    this.selectedStateAreas.some((area, i) => {
      if (area.areaCode === areaCode) {
        areaIndex = i;
        return true;
      }
    });

    if (areaIndex !== -1) {
      this.selectedStateAreas[areaIndex].checked =
        typeof check !== 'undefined' && check !== null
          ? check
          : !this.selectedStateAreas[areaIndex].checked;
    }
  };

  // Get rep areas list
  @action public fetchRepAreasList = async (): Promise<boolean> => {
    try {
      const service = await app.service('reps-assigned');
      const repsList: IRepArea[] = await service.find();

      this.repsAreas = repsList;

      return true;
    } catch (err) {
      console.error(err);

      setTimeout(() => {
        this.fetchRepAreasList();
      }, 512);

      throw err;
    }
  };

  // Get rep assigned areas
  @action public fetchRepAssignedAreas = async (
    rep: ISelectedRep
  ): Promise<boolean> => {
    try {
      if (rep === null) {
        return true;
      }

      const service = await app.service('reps-assigned');
      const areas: IRepAssignedAreaFromApi[] = await service.get(rep.slpcode);

      if (areas.length <= 0) {
        return true;
      }

      const stateCodesIds: string[] = [];
      const states = areas.reduce((combined: IRepAssignedArea[], area) => {
        let stateIndex = stateCodesIds.indexOf(area.state);
        if (stateIndex === -1) {
          stateCodesIds.push(area.state);
          combined.push({
            stateCode: area.state,
            stateLeadsCount: 0,
            areas: [],
          });
          stateIndex = combined.length - 1;
        }

        combined[stateIndex].stateLeadsCount += area.providersCount;
        combined[stateIndex].areas.push({
          areaCode: area.areaCode,
          areaLeadsCount: area.providersCount,
        });

        return combined;
      }, []);

      const index = this.repAssignedAreasDetailsSlpCodes.indexOf(rep.slpcode);
      if (index !== -1) {
        this.repAssignedAreasDetails[index].states = states;
      } else {
        this.repAssignedAreasDetails.push({
          userId: rep.id,
          name: rep.name || rep.username,
          slpcode: rep.slpcode,
          states,
        });
      }

      return true;
    } catch (err) {
      console.error(err);

      setTimeout(() => {
        this.fetchRepAssignedAreas(rep);
      }, 512);

      throw err;
    }
  };

  // Get state list
  @action public fetchStatesList = async (): Promise<any> => {
    try {
      const service = await app.service('provider-state-list');
      const statesList: IStateList[] = await service.find();

      const otherStates: IState[] = [];
      statesList.map((stateDetails, i) => {
        let usaStateIndex = -1;
        const foundState = this.usaStates.some((state, y) => {
          if (stateDetails.plState === state.id) {
            usaStateIndex = y;
            return true;
          }
        });

        if (usaStateIndex !== -1) {
          this.usaStates[usaStateIndex].leadsCount = stateDetails.providerCount;
        } else {
          otherStates.push({
            id: stateDetails.plState,
            name: 'Other',
            leadsCount: stateDetails.providerCount,
          });
        }
      });

      if (otherStates.length > 0) {
        this.usaStates.push(...otherStates);
      }

      return statesList;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Get areas list
  @action public fetchStateAreasList = async (): Promise<any> => {
    try {
      this.selectedStateAreas = [];
      this.selectedStateIsLoading = true;

      const service = await app.service('provider-state-list');
      const stateAreasList: IStateArea[] = await service.get(
        this.selectedState.id
      );

      this.selectedStateAreas = stateAreasList.map<IStateAreaEditable>(
        (area, i) => {
          const assignedTo: IStateAreaUser[] = [];

          if (area.primarySlpcode && area.primaryUserId) {
            assignedTo.push({
              userId: area.primaryUserId,
              name: area.primaryUserName,
              slpcode: area.primarySlpcode,
              primary: 1,
            });
          }

          if (area.secondarySlpcode && area.secondaryUserId) {
            assignedTo.push({
              userId: area.secondaryUserId,
              name: area.secondaryUserName,
              slpcode: area.secondarySlpcode,
              primary: 0,
            });
          }

          return {
            areaCode: area.areaCode,
            providersInAreaCode: area.providersInAreaCode,
            assignedTo,
            checked: false,
            modified: false,
          };
        }
      );

      this.selectedStateIsLoading = false;

      return stateAreasList;
    } catch (err) {
      this.selectedStateIsLoading = false;

      console.error(err);
      throw err;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/State.ts